import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import clsx from 'clsx';
import { useLanguage } from '../../contexts/LanguageContext';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import { 
  ChartPieIcon, 
  AcademicCapIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ReceiptRefundIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import localStorageService from '../../utils/localStorageService';
import toast from 'react-hot-toast';

// Import components for educational institution
import BenefitsOverview from '../../components/benefits/BenefitsOverview';
import TeachersList from '../../components/benefits/TeachersList';
import RevenueAnalysisSimplified from '../../components/benefits/RevenueAnalysisSimplified';
import ExpenseTracker from '../../components/benefits/ExpenseTracker';

const BenefitsPage = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Real-time update indicators
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  const tabs = [
    {
      id: 'overview',
      name: 'Aperçu financier',
      icon: ChartPieIcon,
      component: BenefitsOverview,
      description: 'Vue générale des finances et revenus de formation'
    },
    {
      id: 'teachers',
      name: 'Formateurs',
      icon: AcademicCapIcon,
      component: TeachersList,
      description: 'Gestion des formateurs et leurs performances pédagogiques'
    },
    {
      id: 'revenue',
      name: 'Revenus formations',
      icon: ArrowTrendingUpIcon,
      component: RevenueAnalysisSimplified,
      description: 'Analyse des revenus par cours et formations'
    },
    {
      id: 'expenses',
      name: 'Dépenses éducatives',
      icon: BanknotesIcon,
      component: ExpenseTracker,
      description: 'Gestion des coûts de formation et équipements'
    }
  ];

  const CurrentTabComponent = tabs.find(tab => tab.id === activeTab)?.component || (() => <div>Contenu en développement</div>);

  // Fetch current month's financial summary
  const currentDate = new Date();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  
  // Format dates for API
  const startDate = firstDayOfMonth.toISOString().split('T')[0];
  const endDate = lastDayOfMonth.toISOString().split('T')[0];
  
  // Enhanced real-time financial summary with auto-refresh
  const { data: financialSummary, isLoading, refetch: refetchFinancialSummary } = useQuery(
    ['benefits-financial-summary', startDate, endDate],
    async () => {
      try {
        setIsRefreshing(true);
        console.log('💰 BenefitsPage: Refreshing financial data...');
        
        const payments = localStorageService.getAllItems('payments');
        const courses = localStorageService.getAllItems('courses');
        const sessions = localStorageService.getAllItems('training-sessions');
        const trainers = localStorageService.getAllItems('trainers');
        const students = localStorageService.getAllItems('students');
        const expenses = JSON.parse(localStorage.getItem('expenses') || '[]');

        console.log('📊 Data loaded:', {
          payments: payments.length,
          courses: courses.length,
          sessions: sessions.length,
          trainers: trainers.length,
          students: students.length,
          expenses: expenses.length
        });

        // Filter payments for current month
        const currentMonthPayments = payments.filter(payment => {
          const paymentDate = new Date(payment.createdAt || payment.date);
          return paymentDate >= firstDayOfMonth && paymentDate <= lastDayOfMonth;
        });

        // Calculate revenue - SIMPLIFIED to match top cards exactly
        const totalIncome = currentMonthPayments.reduce((sum, payment) => {
          // Only count PAID payments, only use paidAmount
          if (payment.isPaid || payment.status === 'paid' || payment.status === 'partial') {
            const paidAmount = parseFloat(payment.paidAmount || 0); // ONLY paidAmount, no fallbacks
            return sum + (isNaN(paidAmount) ? 0 : paidAmount);
          }
          return sum;
        }, 0);

        // Calculate teacher costs - SIMPLIFIED to match top cards exactly
        const commissionRate = 0.4; // Fixed 40% commission rate
        const teacherCosts = currentMonthPayments.reduce((total, payment) => {
          // Only count PAID payments, only use paidAmount
          if (payment.isPaid || payment.status === 'paid' || payment.status === 'partial') {
            const paidAmount = parseFloat(payment.paidAmount || 0); // ONLY paidAmount, no fallbacks
            const commission = paidAmount * commissionRate;
            return total + (isNaN(commission) ? 0 : commission);
          }
          return total;
        }, 0);

        // Calculate educational expenses for current month
        const currentMonthExpenses = expenses.filter(expense => {
          const expenseDate = new Date(expense.date || expense.createdAt);
          const isValidDate = !isNaN(expenseDate.getTime());
          return isValidDate && expenseDate >= firstDayOfMonth && expenseDate <= lastDayOfMonth;
        });

        const educationalExpenses = currentMonthExpenses.reduce((total, expense) => {
          const amount = parseFloat(expense.amount || 0);
          return total + (isNaN(amount) ? 0 : amount);
        }, 0);

        const totalExpenses = teacherCosts + educationalExpenses;
        const netProfit = totalIncome - totalExpenses;
        const profitMargin = totalIncome > 0 ? (netProfit / totalIncome) * 100 : 0;

        console.log('🔍 BOTTOM Cards Calculation (BenefitsPage):', {
          totalIncome: totalIncome.toLocaleString() + ' DA',
          teacherCosts: teacherCosts.toLocaleString() + ' DA',
          educationalExpenses: educationalExpenses.toLocaleString() + ' DA',
          totalExpenses: totalExpenses.toLocaleString() + ' DA',
          netProfit: netProfit.toLocaleString() + ' DA',
          profitMargin: profitMargin.toFixed(1) + '%',
          commissionRate: commissionRate,
          paymentsCount: currentMonthPayments.length,
          expensesCount: currentMonthExpenses.length
        });

        // Calculate additional metrics
        const activeStudents = students.filter(s => s.currentCourse).length;
        const activeCourses = courses.filter(c => c.status === 'active' || !c.status).length;

        return {
          data: {
            summary: {
              totalIncome,
              totalExpenses,
              netProfit,
              profitMargin,
              activeStudents,
              activeCourses,
              totalPayments: currentMonthPayments.length
            }
          }
        };
      } catch (error) {
        console.error('Error calculating financial summary:', error);
        toast.error('Erreur lors du calcul des métriques financières');
        return {
          data: {
            summary: {
              totalIncome: 0,
              totalExpenses: 0,
              netProfit: 0,
              profitMargin: 0,
              activeStudents: 0,
              activeCourses: 0,
              totalPayments: 0
            }
          }
        };
      } finally {
        setIsRefreshing(false);
      }
    },
    {
      onError: (error) => {
        console.error('Error fetching financial summary:', error);
        toast.error('Impossible de charger le résumé financier');
        setIsRefreshing(false);
      },
      staleTime: 0, // Always fetch fresh data
      cacheTime: 0, // No cache to force fresh calculations
      refetchInterval: false, // Disable auto-refresh to prevent flashing
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchIntervalInBackground: false
    }
  );

  // Auto-refresh function
  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetchFinancialSummary();
      setLastUpdate(new Date());
      toast.success('Données actualisées avec succès!');
    } catch (error) {
      toast.error('Erreur lors de l\'actualisation');
    } finally {
      setTimeout(() => setIsRefreshing(false), 1000);
    }
  };

  // Stable reference for refetch function
  const stableRefetch = React.useCallback(() => {
    refetchFinancialSummary();
    setLastUpdate(new Date());
  }, [refetchFinancialSummary]);

  // Listen for storage changes (when data is updated in other tabs/pages)
  React.useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'payments' || event.key === 'courses' || event.key === 'sessions' || event.key === 'students') {
        console.log('📡 Storage change detected for:', event.key);
        stableRefetch();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [stableRefetch]);

  // Prepare stats from API data
  const stats = React.useMemo(() => {
    const summary = financialSummary?.data?.summary || {
      totalIncome: 0,
      totalExpenses: 0,
      netProfit: 0,
      profitMargin: 0
    };
    
    return [
      {
        title: 'Revenus Formations',
        value: new Intl.NumberFormat('fr-DZ', {
          style: 'currency',
          currency: 'DZD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(summary.totalIncome),
        icon: BanknotesIcon,
        iconWrap: 'bg-forma-500/15 text-forma-700 dark:bg-forma-500/20 dark:text-forma-200',
      },
      {
        title: 'Coûts Formation',
        value: new Intl.NumberFormat('fr-DZ', {
          style: 'currency',
          currency: 'DZD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(summary.totalExpenses),
        icon: CurrencyDollarIcon,
        iconWrap: 'bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-100',
      },
      {
        title: 'Bénéfice Éducatif',
        value: new Intl.NumberFormat('fr-DZ', {
          style: 'currency',
          currency: 'DZD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(summary.netProfit),
        icon: CheckCircleIcon,
        iconWrap: 'bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-100',
      },
      {
        title: 'Rentabilité Formation',
        value: `${parseFloat(summary.profitMargin).toFixed(1)}%`,
        icon: ArrowTrendingUpIcon,
        iconWrap: 'bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-100',
      },
    ];
  }, [financialSummary]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Finances & revenus"
        description="Tableau de bord financier — indicateurs et onglets mis à jour depuis le stockage local."
        actions={
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className={clsx(
              'inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800',
              isRefreshing && 'cursor-not-allowed opacity-60'
            )}
          >
            <ArrowPathIcon className={clsx('h-5 w-5', isRefreshing && 'animate-spin')} />
            {isRefreshing ? 'Actualisation…' : 'Actualiser'}
          </button>
        }
      />

      <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
        <span className={isRefreshing ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}>
          {isRefreshing ? 'Actualisation…' : 'Données en temps réel'}
        </span>
        <span className="mx-2 text-slate-300 dark:text-slate-600">•</span>
        {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
        <span className="mx-2 text-slate-300 dark:text-slate-600">•</span>
        Dernière màj :{' '}
        {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">{stat.title}</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-50 mt-2">
                  {isLoading ? '...' : stat.value}
                </p>
              </div>
              <div className={clsx('p-3 rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80', stat.iconWrap)}>
                <stat.icon className="h-6 w-6" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <UiCard padding={false} className="overflow-hidden">
        <div className="border-b border-slate-200 bg-white/50 px-2 py-2 dark:border-slate-700 dark:bg-slate-900/40">
        <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setActiveTab(tab.id);
              }}
              className={clsx(
                'group relative flex min-w-max flex-1 flex-col items-center justify-center space-y-1 rounded-2xl px-5 py-3 text-sm font-medium transition-all',
                activeTab === tab.id
                  ? 'bg-forma-600 text-white shadow-md shadow-forma-600/25'
                  : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'
              )}
            >
              <div
                className={clsx(
                  'rounded-xl p-2 transition-all',
                  activeTab === tab.id ? 'bg-white/20' : 'bg-slate-100/80 dark:bg-slate-800/60'
                )}
              >
                <tab.icon className="h-6 w-6" />
              </div>
              <span className="whitespace-nowrap font-semibold">{tab.name}</span>
              <p className="line-clamp-2 hidden max-w-[12rem] text-center text-xs opacity-80 lg:block">
                {tab.description}
              </p>
            </button>
          ))}
        </div>
        </div>

        <div className="border-b border-slate-200 bg-slate-50/90 px-6 py-4 dark:border-slate-600 dark:bg-slate-800/40">
          <div className="flex items-center space-x-3">
            {React.createElement(tabs.find(tab => tab.id === activeTab)?.icon || ChartPieIcon, {
              className: "h-6 w-6 text-forma-600 dark:text-forma-400"
            })}
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">
                {tabs.find(tab => tab.id === activeTab)?.name}
              </h2>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                {tabs.find(tab => tab.id === activeTab)?.description}
              </p>
            </div>
          </div>
        </div>

        <div className="relative p-6">
        <CurrentTabComponent showAddForm={showAddForm} setShowAddForm={setShowAddForm} />
        </div>
      </UiCard>
    </div>
  );
};

export default BenefitsPage;